import { useEffect } from 'react'

import { selectOriginalJourney, selectRoot } from '../store/selectors/config'
import { selectRootDesign } from '../store/selectors/runtime'

import { useAppStoreSelector } from './useAppStoreSelector'

type LegacyTheme = {
  palette: {
    primary: {
      main: string
    }
    background: {
      default: string
    }
  }
}

/**
 * Updates the document manifest for Progressive Web App (PWA) support.
 * This hook listens for changes in the app status and the original journey, generating
 * the manifest details off of it.
 */
export function usePwaManifest() {
  const journey = useAppStoreSelector((state) =>
    selectOriginalJourney(state, selectRoot)
  )
  const design = useAppStoreSelector(selectRootDesign)

  useEffect(() => {
    if (journey?.journeyId) {
      updateDocument({
        name: journey.name,
        logoUrl: design?.logoUrl,
        color: (design?.theme as LegacyTheme)?.palette?.primary?.main,
        backgroundColor: (design?.theme as LegacyTheme)?.palette?.background
          ?.default
      })
    }
  }, [journey?.journeyId, journey?.name, design?.logoUrl, design?.theme])
}

export const updateDocument = (options: {
  name?: string
  logoUrl?: string
  color?: string
  backgroundColor?: string
}) => {
  const { name, logoUrl, color, backgroundColor } = options

  const manifestJSON = {
    // 30 characters is the limit for android & ios
    short_name: name ? `${name.slice(0, 27)}...` : '@epilot',
    name: name ?? '@epilot Journey',
    icons: logoUrl
      ? [
          {
            src: logoUrl
          }
        ]
      : [
          {
            src: 'favicon.ico',
            sizes: '64x64 32x32 24x24 16x16',
            type: 'image/x-icon'
          }
        ],
    start_url: window.location.href,
    display: 'standalone',
    theme_color: color || '#000000',
    background_color: backgroundColor || '#ffffff',
    scope: window.location.origin
  }

  const stringManifest = JSON.stringify(manifestJSON)
  const blob = new Blob([stringManifest], { type: 'application/json' })
  const manifestURL = URL.createObjectURL(blob)

  const htmlTag = document.querySelector('#my-manifest-placeholder')

  if (htmlTag) {
    htmlTag.setAttribute('href', manifestURL)
  }
}
